import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService as Auth0Service, IdToken } from '@auth0/auth0-angular';
import { Observable, ReplaySubject, of } from 'rxjs';
import {
  exhaustMap,
  first,
  map,
  mergeMap,
  reduce,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  IClientFeature,
  IConnectUser,
  IUser,
  IUserClient,
  UserType,
} from '../models/user';
import { AUTH0_DOMAIN, LOCAL_STORAGE } from './token';

@Injectable()
export class CognitoAuthService {
  private redirectKeyName = 'redirect_url';

  loggedInUser: IUser;
  loggedInAuth0$ = new ReplaySubject<boolean>(1);
  loggedInConnect$ = new ReplaySubject<IUser | undefined>(1);
  authError$ = this.auth0Service.error$;

  constructor(
    private http: HttpClient,
    private auth0Service: Auth0Service,
    private router: Router,
    @Inject(LOCAL_STORAGE) private storage: Storage,
    @Inject(AUTH0_DOMAIN) private auth0Domain: string
  ) {}

  initAuth(): Observable<IUser> {
    return this.auth0Service.idTokenClaims$.pipe(
      first(),
      map((claims) => this.getConnectId(claims)),
      tap((connectId) => this.loggedInAuth0$.next(!!connectId)),
      exhaustMap((connectId) => this.getConnectInfoForCreds(connectId)),
      tap((user) => (this.loggedInUser = user)),
      tap((user) => this.loggedInConnect$.next(user))
    );
  }

  logOut(): Observable<void> {
    this.loggedInUser = null;
    this.loggedInConnect$.next(undefined);
    this.loggedInAuth0$.next(false);
    this.setRedirectUrl(this.router.url);
    return this.auth0Service.logout();
  }

  getAccessToken(): Observable<string> {
    return this.auth0Service.getAccessTokenSilently();
  }

  getRedirectUrl(): string {
    return this.storage.getItem(this.redirectKeyName);
  }

  setRedirectUrl(url: string | null): void {
    if (url) {
      this.storage.setItem(this.redirectKeyName, url);
    } else {
      this.storage.removeItem(this.redirectKeyName);
    }
  }

  isClientFeatureEnabled(
    feature: keyof IClientFeature,
    userTypes: UserType[] = ['PM', 'Client']
  ): Observable<boolean> {
    return this.loggedInConnect$.pipe(
      map(
        (user) =>
          Object.values(user?.clients || {}).some(
            (c) => c.features && c.features[feature]
          ) && userTypes.includes(user.userType)
      )
    );
  }

  private getConnectInfoForCreds(connectId?: string): Observable<IUser | null> {
    if (!connectId) {
      return of(null);
    }

    type ClientRes = {
      clientName: string;
      clientId: string;
      features: {
        omnisearchEnabled: boolean;
      };
      contractStatus:
        | 'Prospect'
        | 'Qualifying'
        | 'Qualified'
        | 'Trial'
        | 'Pay as you go'
        | 'Contract'
        | 'One-off usage'
        | 'Lost';
    };

    return this.getConnectData<IConnectUser>([connectId], 'users').pipe(
      map((connectUser) => ({
        ...connectUser,
        id: connectUser.connectId,
      })),
      mergeMap((user) =>
        this.getConnectData<ClientRes>(user.clientIds, 'clients').pipe(
          reduce<ClientRes, Record<string, IUserClient>>(
            (prev, curr) => ({
              ...prev,
              [curr.clientId]: {
                features: curr.features,
                clientName: curr.clientName,
                contractStatus: curr.contractStatus,
              },
            }),
            {}
          ),
          map((clients) => ({ ...user, clients }))
        )
      )
    );
  }

  private getConnectData<T>(ids: string[], resource: string): Observable<T> {
    return this.http
      .post<Record<string, T[]>>(`/data/portal/${resource}/get`, {
        ids,
      })
      .pipe(switchMap((res) => res[resource]));
  }

  private getConnectId(idToken: IdToken): string {
    return (idToken || {})[`https://${this.auth0Domain}/connectId`];
  }
}
